import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { PlaybackState } from '../playback-controller';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../toolbar-control.tokens';
import { VoyageTrackController } from '../voyage-track.controller';
import {
  filterAndSortNearbyRows,
  NearbyRowView,
  NearbyState,
  NEARBY_FALLBACK_MAX_KM,
  VoyageNearbyController,
} from '../voyage-nearby.controller';
import { voyageSpendBlocker } from '../../voyage-radar/voyage-credits.model';
import { BaseControl } from './base-control';
import { relativeFixAge } from './toolbar-vessel-controls';

/**
 * Nearby vessels — the radar button and its drawer.
 *
 * Sibling of `app-port-call-control` and built to the same shape on purpose:
 * same `ToolbarDrawerService`, same standing-affordance visibility, same
 * `.pc-*` row grammar, so the two paid per-vessel drawers read as one product
 * rather than as two features that happen to sit next to each other.
 *
 * What it adds, and why:
 *
 *  - A RADIUS INPUT, because unlike port calls this endpoint is a disc and the
 *    operator chooses how wide. It is asked BEFORE the spend and remembered
 *    afterwards, so a second hull is asked the same question by default.
 *  - TWO POST-LOAD VIEW CONTROLS — a distance cutoff and a sort direction —
 *    which operate purely on rows already in memory. Neither re-fetches and
 *    neither costs anything, and the panel says the count it is showing so a
 *    narrowed view is never mistaken for the whole answer.
 *  - A PER-ROW BUY. `/v1/vessels/nearby` returns no positions, so a row is a
 *    name and a distance until someone pays 1 credit for its fix. That is the
 *    same lookup the POI nearby list already uses — no new spend path.
 */

/**
 * The drawer body. Presentational in the sense that matters — every decision it
 * renders was made in the controller — but it does hold the two view controls,
 * because those change nothing but what is on screen.
 */
@Component({
  selector: 'app-nearby-panel',
  template: `
    <!-- 1. No subject yet. The price rides on the drawer subtitle, so the cost
            is known before a hull is chosen. -->
    <div class="pc-note" *ngIf="!nb.subjectMmsi">
      Select a vessel on the map to see what is around it.
    </div>

    <ng-container *ngIf="nb.subjectMmsi">
      <!-- The radius question. Above the states below because it is what the
           NEXT fetch will ask, not a property of the answer already held. -->
      <div class="nb-ask">
        <label class="nb-ask__label" for="nb-radius">Within</label>
        <input id="nb-radius" class="nb-ask__input" type="number"
               min="0.1" [max]="inputMax" step="1"
               [value]="nb.inputRadiusKm"
               (change)="onRadius($event)"
               [disabled]="nb.loading" />
        <span class="nb-ask__unit">km</span>
      </div>

      <!-- 2. Loading. -->
      <div class="pc-note pc-note--busy" *ngIf="nb.loading">
        <mat-icon class="material-icons-outlined pc-spin">progress_activity</mat-icon>
        Looking for vessels nearby…
      </div>

      <ng-container *ngIf="!nb.loading">
        <!-- 3. Have a hull, never fetched. The button below is the way on. -->
        <div class="pc-note" *ngIf="!isFetched">
          No nearby search loaded for
          {{ nb.subjectName || 'MMSI ' + nb.subjectMmsi }} yet.
        </div>

        <!-- 4. Fetched. A count-0 record renders HERE, not as "not fetched" —
                it is an answer, and with the radius under the operator's own
                control it is a routine one. Dropping back would re-offer a
                spend that already happened. -->
        <ng-container *ngIf="isFetched">
          <div class="pc-meta">
            <ng-container *ngIf="nb.count">
              showing {{ visible.length }} of {{ nb.count }}<ng-container
                *ngIf="nb.radiusKm != null"> within {{ nb.radiusKm }} km</ng-container>
            </ng-container>
            <ng-container *ngIf="!nb.count">
              nothing within {{ nb.radiusKm ?? nb.inputRadiusKm }} km
            </ng-container>
            <ng-container *ngIf="fetchedAgo"> · fetched {{ fetchedAgo }}</ng-container>
          </div>

          <!-- The two view controls. Shown only once there is something to
               narrow, and only when the answer is wide enough for narrowing to
               mean anything. Both are free and neither re-fetches.

               Two lines rather than one: a slider with no label and no readout
               was a bare rail that gave no clue what it did or where it stood,
               and four controls abreast do not fit a 330px card. The label and
               the value sit above the rail they describe; the sort toggle keeps
               the end of that line, where it reads as the other half of "how
               this list is arranged". -->
          <div class="nb-view" *ngIf="nb.count > 1">
            <div class="nb-view__top">
              <label class="nb-view__label" for="nb-cutoff">Show within</label>
              <!-- Fixed width and tabular figures: this text changes on every
                   frame of a drag, and a readout that resized would drag the
                   whole card with it. -->
              <span class="nb-view__val">{{ cutoffLabel }}</span>
              <button type="button" class="nb-view__dir"
                      (click)="toggleDirection()"
                      [matTooltip]="sortTooltip"
                      [attr.aria-label]="sortTooltip">
                <!-- The arrow is the sort direction, and it is why the word
                     alone was not enough: "Nearest" on a button reads equally
                     as the state and as what pressing it would DO. An arrow
                     that flips settles which. -->
                <mat-icon class="material-icons-outlined nb-view__arrow">{{
                  nb.direction === 'nearest' ? 'arrow_upward' : 'arrow_downward'
                }}</mat-icon>
                <span>{{ nb.direction === 'nearest' ? 'Nearest' : 'Farthest' }}</span>
              </button>
            </div>
            <input id="nb-cutoff" class="nb-view__slider" type="range"
                   min="0" [max]="sliderMax" step="0.1"
                   [value]="nb.maxKm"
                   (input)="onCutoff($event)"
                   [attr.aria-valuetext]="cutoffLabel"
                   aria-label="Maximum distance to show" />
          </div>

          <!-- THE ROW IS THE BUTTON, exactly as in the POI target's card: one
               target the width of the drawer instead of a 44px pill at the end
               of it, and no second thing to aim at once the row itself is
               already the thing being chosen. What was the Locate button is now
               a state badge — it never offered anything the row did not. -->
          <div class="pc-list" *ngIf="visible.length">
            <button type="button" class="pc-row nb-row"
                    *ngFor="let r of visible; trackBy: trackRow"
                    [class.nb-row--onmap]="r.onMap"
                    [disabled]="r.pending"
                    [attr.aria-busy]="r.pending ? 'true' : null"
                    [matTooltip]="rowTooltip(r)"
                    (click)="locate(r)">
              <span class="pc-row__text">
                <span class="pc-row__port">{{ r.name }}</span>
                <span class="pc-row__status" *ngIf="r.shipType">{{ r.shipType }}</span>
              </span>
              <span class="nb-row__facts">
                <span class="nb-row__dist" *ngIf="r.distance">{{ r.distance }}</span>
                <span class="nb-row__ais">
                  <ng-container *ngIf="r.speed">{{ r.speed }}</ng-container
                  ><ng-container *ngIf="r.speed && r.course"> · </ng-container
                  ><ng-container *ngIf="r.course">{{ r.course }}</ng-container>
                </span>
              </span>
              <span class="nb-row__badge" *ngIf="r.onMap || r.pending">{{
                r.pending ? '···' : 'On map'
              }}</span>
            </button>
          </div>

          <!-- The row price, stated ONCE and only while some row could still
               spend it — the POI card's footer, for the reason it has one: with
               the whole row clickable there is no labelled button left to carry
               the cost, and the first click must not be the one made without
               it. Rows already on the map fly for free. -->
          <div class="nb-foot" *ngIf="anythingToBuy">
            Click a vessel for its latest position — {{ lookupCostLabel }} each
          </div>

          <!-- A narrowed view that hid everything still has to explain itself,
               or the slider reads as "there is nothing here". -->
          <div class="pc-note" *ngIf="nb.count && !visible.length">
            No vessels within {{ nb.maxKm | number: '1.0-1' }} km. Widen the slider to see the
            rest of what you bought.
          </div>

          <!-- A ceiling is never silent. -->
          <div class="pc-note pc-note--foot" *ngIf="nb.truncated">
            More vessels were in range than one search keeps.
          </div>
        </ng-container>

        <!-- The spend, where the trace drawer puts its own: at the FOOT OF THE
             BODY, under the answer it acts on. It also lands directly under the
             radius input it reads, so the two halves of one act — how far, then
             go — read top to bottom in the order they are performed. In the
             header, as a text action, it was both the wrong weight for the only
             priced control in the card and a long way from the number it
             spends against. -->
        <button type="button" class="tb-drawer__primary tb-drawer__primary--follows"
                [disabled]="!!blocker"
                [matTooltip]="tooltip"
                (click)="fetch()">
          <mat-icon class="material-icons-outlined">radar</mat-icon>
          <span class="tb-drawer__primary-label">{{ actionLabel }}</span>
          <em>{{ nb.inputRadiusKm }} km · {{ costLabel }}</em>
        </button>

        <!-- Same two readings as the trace drawer's cost line, and the same
             reason: nothing stands between this button and the spend, so the
             price and the balance have to be legible where it is pressed. -->
        <div class="tb-drawer__cost">
          <ng-container *ngIf="blocker as why; else haveCredits">
            {{ why }}
          </ng-container>
          <ng-template #haveCredits>
            Uses {{ costLabel }}
            <ng-container *ngIf="nb.creditsRemaining != null">
              &middot; {{ nb.creditsRemaining }} remaining
            </ng-container>
          </ng-template>
        </div>
      </ng-container>
    </ng-container>`,
  styles: [`
    /* A FIXED width, and the reason is the slider.

       app-toolbar-drawer is fluid between 260px and 360px, so it shrink-wraps
       whatever is projected into it. That is right for a panel whose content is
       settled, and wrong for this one: dragging the cutoff adds and removes
       ROWS, each row's name sets its own intrinsic width, and the widest visible
       name is therefore what the card measured itself against — so the drawer
       grew and shrank under the pointer while the rail being dragged moved with
       it. Pinning the panel makes the card one size for the whole drag.

       332px is the drawer's own max (360) less its 14px side padding, so this
       parks it at its natural maximum rather than inventing a new width; the
       max-width keeps it honest if that cap ever tightens. */
    :host {
      display: block;
      width: 332px;
      max-width: 100%;
    }

    /* Deliberately the same .pc-* grammar as the port-call drawer — the two are
       one product. Only the .nb-* additions below are new. */
    .pc-list {
      display: flex;
      flex-direction: column;
      max-height: 260px;
      overflow-y: auto;
    }
    .pc-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 2px;
    }
    .pc-row + .pc-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .pc-row__text {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      line-height: 1.25;
    }
    .pc-row__port {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .pc-row__status {
      font-size: 10.5px;
      opacity: 0.65;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .pc-note {
      font-size: 11.5px;
      opacity: 0.75;
      line-height: 1.4;
      padding: 8px 2px;
      max-width: 260px;
    }
    .pc-note--busy { display: flex; align-items: center; gap: 6px; }
    .pc-note--busy .mat-icon {
      font-size: 15px;
      width: 15px;
      height: 15px;
      line-height: 15px;
    }
    /* Local spinner, not the toolbar host's: this panel renders into a CDK
       overlay, outside the host's DOM subtree. */
    .pc-spin { animation: pc-spin 1.1s linear infinite; }
    @keyframes pc-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) {
      .pc-spin { animation: none; }
    }
    .pc-note--foot {
      border-top: 1px solid rgba(128, 128, 128, 0.18);
      padding-top: 8px;
    }
    .pc-meta {
      font-size: 11px;
      opacity: 0.65;
      font-variant-numeric: tabular-nums;
      padding: 0 2px 6px;
    }

    .nb-ask {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 2px 8px;
      font-size: 11.5px;
    }
    .nb-ask__label { opacity: 0.75; }
    .nb-ask__input {
      width: 64px;
      font: inherit;
      font-variant-numeric: tabular-nums;
      padding: 3px 6px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 4px;
      background: transparent;
      color: inherit;
    }
    .nb-ask__input:disabled { opacity: 0.5; }
    .nb-ask__unit { opacity: 0.75; }

    .nb-view { padding: 0 2px 6px; }
    .nb-view__top {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-bottom: 3px;
      font-size: 11.5px;
    }
    .nb-view__label { opacity: 0.75; flex: 1 1 auto; min-width: 0; }
    /* Fixed width, right-aligned, tabular figures. This value updates on every
       pointermove of a drag, and proportional digits of differing widths would
       shift the sort button beside it on almost every frame. */
    .nb-view__val {
      flex: 0 0 auto;
      min-width: 54px;
      text-align: right;
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }
    .nb-view__slider {
      display: block;
      width: 100%;
      margin: 0;
      accent-color: #2563EB;
    }
    .nb-view__dir {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      font: inherit;
      font-size: 10.5px;
      padding: 2px 8px 2px 5px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 999px;
      background: transparent;
      color: inherit;
      cursor: pointer;
    }
    .nb-view__dir:hover { border-color: #2563EB; color: #2563EB; }
    .nb-view__arrow {
      font-size: 13px;
      width: 13px;
      height: 13px;
      line-height: 13px;
    }

    .nb-row__facts {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      line-height: 1.25;
      white-space: nowrap;
    }
    .nb-row__dist {
      font-size: 11.5px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
    .nb-row__ais { font-size: 10.5px; opacity: 0.65; font-variant-numeric: tabular-nums; }

    /* The row IS the button now, so it has to shed the UA button look — a
       centred, bordered, system-font block — and take the row layout the
       neighbouring .pc-row keeps. text-align is the one that bites: a button
       centres its text where a div did not. */
    .nb-row {
      width: 100%;
      font: inherit;
      text-align: left;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      border-radius: 6px;
      transition: background 0.12s ease;
    }
    .nb-row:hover:not(:disabled) { background: rgba(37, 99, 235, 0.08); }
    .nb-row:focus-visible { outline: 2px solid #2563EB; outline-offset: -2px; }
    /* Already placed: still clickable — it flies to the hull — but it must not
       read as an offer to buy, so it takes no emphasis. */
    .nb-row--onmap { opacity: 0.72; }
    .nb-row:disabled { opacity: 0.5; cursor: default; }
    .nb-row__badge {
      flex: 0 0 auto;
      font-size: 10px;
      letter-spacing: 0.02em;
      opacity: 0.7;
      white-space: nowrap;
      /* Reserves the badge column so a row gaining "On map" does not reflow the
         two beside it — the same reason the cutoff readout is fixed-width. */
      min-width: 46px;
      text-align: right;
    }

    /* The row price. Sits below the scroller rather than inside it: at the
       bottom of a 260px list it would be under the fold on the first open,
       which is the one time it needs reading. */
    .nb-foot {
      border-top: 1px solid rgba(128, 128, 128, 0.18);
      margin-top: 4px;
      padding: 7px 2px 0;
      font-size: 10.5px;
      opacity: 0.7;
      line-height: 1.4;
    }

    /* :host-context, NOT a bare .dark-theme descendant selector. The theme class
       sits on an ancestor OUTSIDE this component, so the descendant form is dead
       CSS here — and this panel renders into a CDK overlay, further from the
       host than the styles it copies. */
    :host-context(.dark-theme) .nb-ask__input,
    :host-context(.dark-theme) .nb-view__dir {
      border-color: rgba(255, 255, 255, 0.28);
    }
    :host-context(.dark-theme) .nb-row:hover:not(:disabled) {
      background: rgba(96, 165, 250, 0.14);
    }
  `],
})
export class NearbyPanelComponent {
  /** Everything comes off the nearby state — including WHICH hull. */
  @Input() nb!: NearbyState;

  constructor(private controller: VoyageNearbyController) {}

  /**
   * "Fetched" means fetched FOR THIS HULL. A count-0 answer still counts — it is
   * an answer and it was free, which is what stops the drawer dropping back to
   * "not loaded yet" and re-offering a spend that already happened.
   */
  get isFetched(): boolean {
    return !!this.nb.subjectMmsi && this.nb.fetchedForMmsi === this.nb.subjectMmsi;
  }

  /** Held rows put through the two view controls. Free, in-memory, no re-fetch. */
  get visible(): NearbyRowView[] {
    return filterAndSortNearbyRows(this.nb.rows, {
      maxKm: this.nb.maxKm,
      direction: this.nb.direction,
    });
  }

  /**
   * The slider's ceiling is the radius ACTUALLY SERVED, never the value typed.
   * The two differ whenever the platform clamped, and a slider that ran to the
   * typed value would imply rows exist beyond what was bought.
   */
  get sliderMax(): number {
    return this.nb.radiusKm ?? this.nb.inputRadiusKm;
  }

  /**
   * The input's ceiling comes from the last answer for the same reason, falling
   * back to the platform radius only before the first one. Every response echoes
   * what it served, and that value wins — so lowering the server's radius takes
   * effect without a client release.
   */
  get inputMax(): number {
    return this.nb.radiusKm ?? NEARBY_FALLBACK_MAX_KM;
  }

  get fetchedAgo(): string | null {
    return this.nb.fetchedAt ? relativeFixAge(this.nb.fetchedAt) : null;
  }

  /**
   * The cutoff as text, at ONE decimal at most — the slider steps in 0.1s, and
   * the raw float renders as "7.300000000000001" often enough to matter.
   */
  get cutoffLabel(): string {
    const km = Math.round(this.nb.maxKm * 10) / 10;
    return `${km} km`;
  }

  /**
   * Names the state AND the act, because the button can only show one of them:
   * "Sorted nearest first" is what the arrow means, "click for farthest" is what
   * pressing it does.
   */
  get sortTooltip(): string {
    return this.nb.direction === 'nearest'
      ? 'Sorted nearest first — click for farthest'
      : 'Sorted farthest first — click for nearest';
  }

  get lookupCostLabel(): string {
    const n = this.nb.lookupCredits;
    return `${n} ${n === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * Is there any row a click could still charge for? Priced on the WHOLE held
   * answer rather than on the rows currently visible — the same rule the POI
   * card follows — so the footer does not blink out mid-drag just because the
   * cutoff happens to be hiding every unplaced hull.
   */
  get anythingToBuy(): boolean {
    return (this.nb.rows ?? []).some((r) => !r.onMap);
  }

  /** What THIS row's click will do, which differs by whether it is placed. */
  rowTooltip(r: NearbyRowView): string {
    if (r.pending) return 'Fetching this vessel’s latest position…';
    if (r.onMap) return 'Already on the map — go to it';
    return `Fetch latest position and place on the map · ${this.lookupCostLabel}`;
  }

  get costLabel(): string {
    const n = this.nb.nearbyCredits;
    return `${n} ${n === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * "Search again" once an answer is held — not "Refresh". The radius input
   * above sits between the two presses, so the second one is frequently a
   * DIFFERENT question rather than the same one asked afresh, and it is billed
   * as one either way.
   */
  get actionLabel(): string {
    return this.isFetched ? 'Search again' : 'Find nearby';
  }

  get tooltip(): string {
    return `Ask the operator which vessels are within ${this.nb.inputRadiusKm} km of this one`;
  }

  /**
   * What this button must go DEAD for, or null when it stays live — via
   * `voyageSpendBlocker` rather than an inline comparison, so every priced
   * Voyage Radar control draws the same line in the same place. An empty wallet
   * blocks; an ended TOI does not, staying pressable so `fetchNearbyVessels`
   * can answer the press with the modal. An UNKNOWN balance (null) means it was
   * never reported, not that it is spent, and `null < cost` is TRUE in JS — a
   * naive test would refuse a user the server would allow.
   */
  get blocker(): string | null {
    return voyageSpendBlocker({
      action: 'nearby',
      cost: this.nb.nearbyCredits,
      remaining: this.nb.creditsRemaining,
      toiEnded: this.nb.toiEnded,
    });
  }

  /**
   * Spend. Deliberately does NOT close the drawer the way the trace panel does:
   * a track is drawn on the map behind the card, so dismissing it is what
   * reveals the result, whereas these rows land in this very panel. The
   * in-flight guard is the controller's own `loading`, which also swaps this
   * button for the busy note above.
   */
  fetch(): void {
    this.controller.fetch(this.nb.subjectMmsi, this.nb.subjectName, this.nb.inputRadiusKm);
  }

  onRadius(e: Event): void {
    this.controller.setInputRadiusKm(Number((e.target as HTMLInputElement).value));
  }

  onCutoff(e: Event): void {
    this.controller.setMaxKm(Number((e.target as HTMLInputElement).value));
  }

  toggleDirection(): void {
    this.controller.setDirection(this.nb.direction === 'nearest' ? 'farthest' : 'nearest');
  }

  locate(r: NearbyRowView): void {
    this.controller.pickVessel(r.mmsi);
  }

  trackRow(i: number, r: NearbyRowView): string {
    return r.mmsi;
  }
}

@Component({
  selector: 'app-nearby-control',
  template: `
    <!-- The wrapper is how the tooltip reaches the price, which lives in
         controller state. NOT a visibility condition: a BehaviorSubject emits
         synchronously, so this always renders and the button stays a standing
         affordance next to its port-call sibling. -->
    <ng-container *ngIf="nearby.state$ | async as ns">
      <button #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Nearby vessels"
              [matTooltip]="'Nearby vessels · ' + costLabel(ns)"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">radar</mat-icon>
      </button>
    </ng-container>

    <ng-template #panel>
      <ng-container *ngIf="nearby.state$ | async as ns">
        <ng-container *ngIf="track.state$ | async as ts">
          <!-- No actionLabel binding: the spend is a primary button at the foot
               of the panel body, where the trace drawer and the port-call
               drawer both put theirs. The header is chrome only. -->
          <app-toolbar-drawer
              [title]="drawerTitle(ns)"
              [subtitle]="drawerSubtitle(ns)"
              [accent]="accentFor(ts, ns)"
              (close)="drawer.close()">
            <app-nearby-panel [nb]="ns"></app-nearby-panel>
          </app-toolbar-drawer>
        </ng-container>
      </ng-container>
    </ng-template>`,
})
export class NearbyControlComponent extends BaseControl implements AfterViewInit, OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;
  @ViewChild('trigger') triggerRef!: ElementRef;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    /** "Which vessel is active" — the same stream Trace and port calls read. */
    public track: VoyageTrackController,
    /** Rows, loading, the radius, and the price. */
    public nearby: VoyageNearbyController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  /**
   * The popup's Nearby action OPENS this drawer rather than spending on its own,
   * so a card opened by accident can never buy anything. `ngAfterViewInit`, not
   * the constructor — `triggerRef` does not exist until the view is built.
   */
  private _openFn = () => this.drawer.open(this.panel, this.vcr, this.triggerRef);

  ngAfterViewInit(): void {
    this.nearby.onRequestOpen = this._openFn;
  }

  costLabel(ns: NearbyState): string {
    return `${ns.nearbyCredits} ${ns.nearbyCredits === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * The selection colour, but only while the drawer's subject IS the selected
   * vessel — a popup-opened subject that was never selected has no colour on the
   * map, and borrowing another hull's would point at the wrong arrow.
   */
  accentFor(ts: PlaybackState, ns: NearbyState): string | null {
    return ts.activeMmsi && ts.activeMmsi === ns.subjectMmsi ? ts.activeVesselColor ?? null : null;
  }

  drawerTitle(ns: NearbyState): string {
    if (ns.subjectName) return ns.subjectName;
    return ns.subjectMmsi ? `MMSI ${ns.subjectMmsi}` : 'Nearby vessels';
  }

  drawerSubtitle(ns: NearbyState): string | null {
    if (!ns.subjectMmsi) return `${this.costLabel(ns)} per search`;
    return ns.subjectName ? ns.subjectMmsi : null;
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
    // Only clear it if THIS instance owns it: a slot swap (live → track) tears
    // one instance down and stands another up, and the new one's own
    // ngAfterViewInit must win rather than lose to the old one's cleanup.
    if (this.nearby.onRequestOpen === this._openFn) this.nearby.onRequestOpen = null;
  }
}
